import { useEffect } from 'react';
import { AgGridEvent, GridApi } from 'ag-grid-community';

import { ScenarioDetailRow, useScenarioMetadata } from '@queries/scenarios';
import type { GetEntityId } from '../../../shared/gridEdit/useEditableGridLogic';
import { ReportRowData } from '../types';
import { LABEL_COLUMN_ID } from '../constants';
import keyBy from 'lodash/keyBy';

export const getDetailColumnWidth = ({
  parentGridApi,
  gridApi,
}: {
  parentGridApi: GridApi,
  gridApi?: GridApi,
}) => {
  const reportDescriptionColumnWidth = parentGridApi?.getColumn(LABEL_COLUMN_ID)?.getActualWidth() ?? 0;
  const accountsColumnWidth = gridApi?.getColumn('account')?.getActualWidth() ?? 0;
  const adminColumnWidth = gridApi?.getColumn('adminCol')?.getActualWidth() ?? 0;

  return reportDescriptionColumnWidth - accountsColumnWidth - adminColumnWidth;
};

export const getFieldName = (reportRow: ReportRowData, reportName: string) => {
  if (typeof reportRow.from === 'string') {
    return reportRow.from;
  }
  return reportRow.from?.[reportName] ?? '';
};

export const getScenarioReportRowDetailId: GetEntityId<ScenarioDetailRow> = row => row.meta.isAdded
  ? null
  : row.rowKey!;

export const createDetailRow = (): ScenarioDetailRow => ({
  account: '',
  detailName: '',
  details: {},
});

export const useGridApiEventListener = <T extends AgGridEvent>(
  emitter: GridApi | null | undefined,
  event: string,
  callback: (event: T) => void
) => {
  useEffect(() => {
    emitter?.addEventListener(event as any, callback);
    return () => emitter?.removeEventListener(event as any, callback);
  }, [emitter, event, callback]);
};

type GetDetailKeyParams = {
  scenarioId: string | number,
  companyCode: string,
  countryTaxType: string,
}
export const getDetailKey = <T extends GetDetailKeyParams>({
  scenarioId,
  companyCode,
  countryTaxType,
}: T) => `${scenarioId}:${companyCode}:${countryTaxType}`;

export const useScenariosMetadata = ({
  baseScenarioId,
  compareScenarioId,
}: {
  baseScenarioId: number,
  compareScenarioId?: number | null,
}) => {
  const { data: baseScenario, isFetching: isLoadingBaseScenario } = useScenarioMetadata({ scenarioId: baseScenarioId });
  const { data: compareScenario, isFetching: isLoadingCompareScenario } = useScenarioMetadata(compareScenarioId
    ? { scenarioId: compareScenarioId }
    : { enabled: false }
  );

  const scenariosById = keyBy([baseScenario, compareScenario].filter(Boolean), 'scenarioId');

  return {
    scenariosById,
    isLoading: isLoadingBaseScenario || isLoadingCompareScenario,
  };
};
